import { useCallback } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { useMount } from "app/custom-hooks/useMount";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { BaseTab } from "../types";

const DEFAULT_TAB_INDEX = 0;

type UseTabSelectionParams<Tab> = {
    tabs: readonly Tab[];
    /** When true, the URL route query param is the source of truth */
    routeSync?: boolean;
    /** External selected tab id. Only used when `routeSync` is false */
    selectedTabId?: string | null;
    onTabSelect?: (tabId: string) => void;
    onTabClick?: (tabId: string) => void;
};

/**
 * Two mutually exclusive modes control which tab is active:
 *
 * - `routeSync: true` — tab index is derived from the route query param
 *   each tab change writes back to the URL. `selectedTabId` is ignored.
 *
 * - `routeSync: false` (default) — tab index is driven by `selectedTabId` (falls back
 *   to route query param for the initial render, then clears it from the URL).
 *   each tab change does NOT touch the URL; the parent owns the selected state.
 */
export const useTabSelection = <Id extends string, Tab extends BaseTab<Id> = BaseTab<Id>>({
    tabs,
    selectedTabId,
    routeSync = false,
    onTabSelect,
    onTabClick,
}: UseTabSelectionParams<Tab>): [number, (tabIndex: number) => void] => {
    const { params } = useCurrentStateAndParams<{ pageSection: Id | null }>();

    const resolveTabIndex = (): number => {
        if (!routeSync && selectedTabId) {
            return Math.max(
                tabs.findIndex((t) => t.id === selectedTabId),
                DEFAULT_TAB_INDEX,
            );
        }

        return Math.max(
            tabs.findIndex(({ id }) => id === params.pageSection),
            DEFAULT_TAB_INDEX,
        );
    };

    const selectedTabIndex = resolveTabIndex();

    useMount(() => {
        onTabSelect?.(tabs[selectedTabIndex].id);

        if (!routeSync && params.pageSection) {
            setTimeout(() => {
                proNavigator.updateQueryParams({ pageSection: null }, { reload: false, location: "replace" });
            });
        }
    });

    const handleTabChange = useCallback(
        (tabIndex: number): void => {
            const tab = tabs[tabIndex] ?? tabs[DEFAULT_TAB_INDEX];

            if (routeSync) {
                proNavigator.updateQueryParams({ pageSection: tab.id || null }, { reload: false });
            }

            onTabSelect?.(tab.id);
            onTabClick?.(tab.id);
        },
        [tabs, routeSync, onTabSelect, onTabClick],
    );

    return [selectedTabIndex, handleTabChange];
};
